import { z } from 'zod';
import { AutonomyClass, SideEffectClass } from './actions.ts';
import { IsoDateTime } from './common.ts';

/**
 * Added by 21 step 0 (brief 21 §1b.5, ADR-021): who originates the governed call — platform core or a pack module.
 * Derived from the trusted execution context (the workload's module), never from the request.
 */
export const PolicyOrigin = z.enum(['platform', 'pack']).meta({ id: 'PolicyOrigin' });
export type PolicyOrigin = z.infer<typeof PolicyOrigin>;

export const ArgConstraint = z
  .object({
    /** Dotted path into the action args, e.g. `amount` or `supplier.country`. */
    path: z.string().min(1),
    op: z.enum(['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'in', 'matches']),
    value: z.unknown(),
  })
  .meta({ id: 'ArgConstraint' });
export type ArgConstraint = z.infer<typeof ArgConstraint>;

/** One policy rule. All present `when` conditions must match. Deny overrides allow. */
export const PolicyRule = z
  .object({
    id: z.string().min(1),
    effect: z.enum(['allow', 'deny']),
    description: z.string().default(''),
    when: z
      .object({
        roles: z.array(z.string()).optional(),
        /** Tool name globs, e.g. `supplier.*`. */
        tools: z.array(z.string()).optional(),
        autonomyClasses: z.array(AutonomyClass).optional(),
        sideEffectClasses: z.array(SideEffectClass).optional(),
        domains: z.array(z.string()).optional(),
        environments: z.array(z.string()).optional(),
        /** When true, a matching allow carries obligation `approval_required` unless an approval is present. */
        requireApproval: z.boolean().optional(),
        args: z.array(ArgConstraint).optional(),
        targetTypes: z.array(z.string()).optional(),
        /**
         * Matches only when an approval is (true) / is not (false) present. A deny rule with
         * `approvalPresent: false` also carries obligation `approval_required` (an approval lifts it).
         */
        approvalPresent: z.boolean().optional(),
        /** Matches only when the tool's or the agent's kill switch is engaged (true) / released (false). */
        killSwitch: z.boolean().optional(),
        /** Added by 21 step 0: matches when the call's origin (from the execution context) is listed. */
        origins: z.array(PolicyOrigin).optional(),
        /**
         * Added by 21 step 0: matches when the call carries a notification `templateKey` that is listed. The value
         * `$notifications.approvalExempt` stands for the profile list `notifications.approvalExempt`.
         */
        templateKeys: z.array(z.string().min(1)).optional(),
      })
      .prefault({}),
    /**
     * Added by 21b (brief 21 §1b.5): the rule does **not** match when every present condition here holds. Lets
     * `platform-base` keep "external communication needs approval" while exempting platform-origin sends of the
     * `notifications.approvalExempt` template keys, without a negation operator in `when`.
     */
    exceptWhen: z
      .object({
        origins: z.array(PolicyOrigin).optional(),
        templateKeys: z.array(z.string().min(1)).optional(),
        tools: z.array(z.string()).optional(),
      })
      .optional(),
  })
  .meta({ id: 'PolicyRule' });
export type PolicyRule = z.infer<typeof PolicyRule>;

/** A versioned policy bundle registered by a pack (or the platform's `platform-base`). */
export const PolicyBundle = z
  .object({
    name: z.string().min(1),
    version: z.string().min(1),
    description: z.string().optional(),
    rules: z.array(PolicyRule),
  })
  .meta({ id: 'PolicyBundle' });
export type PolicyBundle = z.infer<typeof PolicyBundle>;
export type PolicyBundleInput = z.input<typeof PolicyBundle>;

/** Serialisable outcome of a policy evaluation. */
export const PolicyDecision = z
  .object({
    allow: z.boolean(),
    reasons: z.array(z.string()),
    obligations: z.array(z.string()),
    policyVersion: z.string(),
    evaluatedAt: IsoDateTime,
  })
  .meta({ id: 'PolicyDecision' });
export type PolicyDecision = z.infer<typeof PolicyDecision>;

/** Added by 21 step 0: placeholder in `when.templateKeys` for the profile list `notifications.approvalExempt`. */
export const APPROVAL_EXEMPT_TEMPLATES_REF = '$notifications.approvalExempt';
